'use client'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { Box, InputAdornment, useMediaQuery, useTheme } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import 'dayjs/locale/vi'
import { useRef } from 'react'
import { Control, Controller, FieldValues, useFormContext, useFormState } from 'react-hook-form'
import { ErrorTooltipIcon, type ErrorTooltipIconRef, FieldLabel } from '../rhf-text-field/components'
import { getErrorBorderStyles } from '../rhf-text-field/styles'
type Props = {
  picker?: 'date' | 'week' | 'month' | 'quarter' | 'year'
  placeholder?: string
  control?: Control<FieldValues, any>
  name: string
  label?: string
  required?: boolean
  className?: string
  disabledDates?: string[]
  mode?: 'future' | 'past'
}
const weekdays = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']
export const RHFDatePicker = ({
  label,
  picker,
  required,
  className,
  placeholder,
  name,
  disabledDates,
  mode,
}: Props) => {
  const { control } = useFormContext()
  const { isSubmitted } = useFormState({ control })
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const errorIconRef = useRef<ErrorTooltipIconRef>(null)

  return (
    <Box position={'relative'} width={'100%'}>
      {label && <FieldLabel label={label} required={required} />}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const handleInputClick = () => {
            // Ẩn tooltip khi click vào input
            if (errorIconRef.current) {
              errorIconRef.current.hideTooltip()
            }
          }
          return (
            <DemoContainer
              components={['DatePicker']}
              sx={{
                padding: 0,
              }}
            >
              <DesktopDatePicker
                views={['year', 'month', 'day']}
                {...field}
                value={!!field.value ? dayjs(field.value) : null}
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  ...getErrorBorderStyles(!!error),
                }}
                slots={{
                  openPickerIcon: () => (
                    <>
                      {/* Error Icon với MUI Tooltip (Desktop only) */}
                      {error && isDesktop && (
                        <ErrorTooltipIcon
                          ref={errorIconRef}
                          errorMessage={error.message || ''}
                          showTooltip={isDesktop}
                          isSubmitted={isSubmitted}
                        />
                      )}
                      <CalendarMonthIcon className={`!w-[.8em] ${!!error ? 'text-five' : 'text-primary'}`} />
                    </>
                  ),
                }}
                onChange={(date) => {
                  if (!date) {
                    field.onChange(null)
                    return
                  }
                  if (mode === 'future' && date.isAfter(dayjs(), 'day')) {
                    return
                  }
                  if (mode === 'past' && date.isBefore(dayjs(), 'day')) {
                    return
                  }
                  const formattedDate = dayjs(date).format('YYYY-MM-DD')
                  field.onChange(formattedDate)
                }}
                // format="YYYY-MM-DD"
                dayOfWeekFormatter={(day) => weekdays[day.day()] ?? day.format('dd')}
                formatDensity={'dense'}
                slotProps={{
                  textField: {
                    // Show helperText only on mobile
                    helperText: !isDesktop ? error?.message : undefined,
                    error: !!error,
                    onClick: handleInputClick,
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiPickersInputBase-root': {
                        borderRadius: '9999px',
                      },
                    },
                  },
                }}
                shouldDisableDate={(date) => {
                  if (disabledDates) {
                    return disabledDates.some((d) => date.isSame(dayjs(d), 'day'))
                  }
                  if (mode === 'future') {
                    return date.isAfter(dayjs(), 'day')
                  }
                  if (mode === 'past') {
                    return date.isBefore(dayjs(), 'day')
                  }
                  return false
                }}
              />
            </DemoContainer>
          )
        }}
      />
    </Box>
  )
}
